/**
 * Firebase Authentication Service
 *
 * Provides sign-in, sign-out, and token management functions
 * for Firebase Authentication.
 *
 * Key Features:
 * - Microsoft (Entra ID) sign-in via popup
 * - Email/Password sign-in
 * - Custom claims management
 * - Token refresh utilities
 */

import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signOut as firebaseSignOut,
  fetchSignInMethodsForEmail,
  OAuthProvider,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth } from './firebaseConfig';
import { googleProvider, microsoftProvider } from './authProviders';

/**
 * Custom claims structure set by the backend API
 */
export interface UserClaims {
  role?: string;
  organisationId?: string;
  organisationSlug?: string;
}

/**
 * Sign in with Microsoft (Entra ID)
 *
 * Opens a popup window for Microsoft authentication.
 * This is the primary authentication method for enterprise users.
 *
 * @returns The authenticated Firebase user
 * @throws FirebaseError if authentication fails
 */
export async function signInWithMicrosoft(): Promise<User> {
  console.info('[AuthService] Starting Microsoft sign-in...');
  console.info('[AuthService] Current auth state before sign-in:', {
    currentUser: auth.currentUser ? {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      providerData: auth.currentUser.providerData.map(p => ({
        providerId: p.providerId,
        uid: p.uid,
        email: p.email,
      })),
    } : null,
  });

  try {
    const result: UserCredential = await signInWithPopup(auth, microsoftProvider);
    console.info('[AuthService] Microsoft sign-in successful:', result.user.email);
    return result.user;
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'auth/account-exists-with-different-credential') {
      const email = (error.customData?.email as string | undefined) ?? '(unknown)';
      const attemptedCredential = OAuthProvider.credentialFromError(error);

      console.error('[AuthService] account-exists-with-different-credential diagnostic:', {
        errorCode: error.code,
        conflictingEmail: email,
        attemptedProviderId: attemptedCredential?.providerId ?? '(could not extract)',
        attemptedSignInMethod: attemptedCredential?.signInMethod ?? '(could not extract)',
      });

      try {
        const existingMethods = await fetchSignInMethodsForEmail(auth, email);
        console.error('[AuthService] Sign-in methods registered for this email in Firebase:', existingMethods);
        console.error('[AuthService] Hint: if "microsoft.com" is NOT in the list above, the account was created with a different provider and is blocking this sign-in.');
      } catch (fetchErr) {
        console.error('[AuthService] Could not fetch existing sign-in methods:', fetchErr);
      }
    }
    throw error;
  }
}

/**
 * Sign in with Google
 *
 * Opens a popup window for Google authentication.
 *
 * @returns The authenticated Firebase user
 * @throws FirebaseError if authentication fails
 */
export async function signInWithGoogle(): Promise<User> {
  console.info('[AuthService] Starting Google sign-in...');

  const result: UserCredential = await signInWithPopup(auth, googleProvider);

  console.info('[AuthService] Google sign-in successful:', result.user.email);
  return result.user;
}

/**
 * Sign in with Email and Password
 *
 * Standard email/password authentication for users without
 * Microsoft accounts.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns The authenticated Firebase user
 * @throws FirebaseError if authentication fails
 */
export async function signInWithEmailPassword(
  email: string,
  password: string
): Promise<User> {
  console.info('[AuthService] Starting email sign-in for:', email);
  
  const result: UserCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );
  
  console.info('[AuthService] Email sign-in successful');
  return result.user;
}

/**
 * Create a new user with Email and Password
 *
 * Registers a new Firebase user. After registration, the user
 * must be provisioned in the backend to receive custom claims.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns The newly created Firebase user
 * @throws FirebaseError if registration fails
 */
export async function registerWithEmailPassword(
  email: string,
  password: string
): Promise<User> {
  console.info('[AuthService] Starting email registration for:', email);
  
  const result: UserCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  
  console.info('[AuthService] Email registration successful');
  return result.user;
}

/**
 * Send a password reset email
 *
 * Sends an email to the specified address with a link to reset
 * their password. The email must be associated with an existing account.
 *
 * @param email - User's email address
 * @throws FirebaseError if the email is not found or invalid
 */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  console.info('[AuthService] Sending password reset email to:', email);
  await firebaseSendPasswordResetEmail(auth, email);
  console.info('[AuthService] Password reset email sent');
}

/**
 * Sign out the current user
 *
 * Clears the Firebase authentication session.
 */
export async function signOut(): Promise<void> {
  console.info('[AuthService] Signing out...');
  await firebaseSignOut(auth);
  console.info('[AuthService] Sign-out complete');
}

/**
 * Get custom claims from the user's ID token
 *
 * Custom claims are set by the backend API and include:
 * - role: User's role within the organisation
 * - organisationId: UUID of the user's organisation
 * - organisationSlug: URL-safe slug of the organisation
 *
 * @param user - Firebase user object
 * @returns Custom claims object
 */
export async function getUserClaims(user: User): Promise<UserClaims> {
  const idTokenResult = await user.getIdTokenResult();
  
  return {
    role: idTokenResult.claims.role as string | undefined,
    organisationId: idTokenResult.claims.organisationId as string | undefined,
    organisationSlug: idTokenResult.claims.organisationSlug as string | undefined,
  };
}

/**
 * Force refresh the user's ID token
 *
 * This should be called after the backend sets custom claims
 * to ensure the frontend has the latest token.
 *
 * @param user - Firebase user object
 */
export async function refreshUserToken(user: User): Promise<void> {
  console.info('[AuthService] Refreshing user token...');
  await user.getIdToken(true); // Force refresh
  console.info('[AuthService] Token refreshed');
}

/**
 * Call the backend API to set custom claims
 *
 * After Firebase authentication, this endpoint sets the user's
 * role and organisation claims on their Firebase token.
 *
 * @param user - Firebase user object
 * @throws UserNotAuthorizedError if user is not provisioned (403)
 * @throws Error if the API call fails for other reasons
 */
export class UserNotAuthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserNotAuthorizedError';
  }
}

export class AuthBackendUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthBackendUnavailableError';
  }
}

export async function callSetClaimsEndpoint(user: User): Promise<void> {
  console.info('[AuthService] Calling set-claims endpoint...');
  
  const token = await user.getIdToken();
  const apiUrl = import.meta.env.VITE_API_URL;
  
  if (!apiUrl) {
    throw new Error('VITE_API_URL environment variable is not set');
  }
  
  let response: Response;

  try {
    response = await fetch(`${apiUrl}/v1/auth/set-claims`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('[AuthService] Could not reach set-claims endpoint:', error);
    throw new AuthBackendUnavailableError(
      `Could not reach the authentication API at ${apiUrl}. Start the backend (pnpm run api) and try again.`
    );
  }
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('[AuthService] Set-claims failed:', response.status, errorText);
    
    // 403 = user authenticated but not authorized (not provisioned in system)
    if (response.status === 403) {
      throw new UserNotAuthorizedError(
        'You are not authorized to access this system. Please contact an administrator.'
      );
    }
    
    // 404 = user or organisation not found
    if (response.status === 404) {
      throw new UserNotAuthorizedError(
        'Your account is not set up. Please contact an administrator.'
      );
    }
    
    throw new Error(`Failed to set claims: ${response.statusText}`);
  }
  
  console.info('[AuthService] Custom claims set successfully');
}

/**
 * Check if a user has custom claims set
 *
 * Useful for determining if the user needs to complete
 * the claims setup flow.
 *
 * @param user - Firebase user object
 * @returns True if the user has a role claim
 */
export async function hasCustomClaims(user: User): Promise<boolean> {
  const claims = await getUserClaims(user);
  return !!claims.role;
}
